/**
 * Bitcoin Network Type Module
 *
 * This module provides type-safe handling of Bitcoin network types (mainnet, testnet, regtest).
 * It includes bidirectional mappings between uppercase keys and lowercase values, along with
 * utility functions for network detection.
 */

// ============================================================================
// 1. ENUM: Source of Truth for Network Types
// ============================================================================

/**
 * All supported Bitcoin network types.
 * - MAINNET: Production Bitcoin network
 * - TESTNET: Public test network for developers
 * - REGTEST: Local regression testing network
 */
export enum NetworkType {
  MAINNET = 'mainnet',
  TESTNET = 'testnet',
  REGTEST = 'regtest',
}

// ============================================================================
// 2. TYPE: Union of Enum Values (lowercase)
// ============================================================================

/**
 * Union type of all network type values (lowercase strings).
 * Equivalent to: "mainnet" | "testnet" | "regtest"
 */
export type NetworkTypeValue = `${NetworkType}`;

// ============================================================================
// 3. TYPE: Union of Enum Keys (uppercase)
// ============================================================================

/**
 * Union type of all network type keys (uppercase strings).
 * Equivalent to: "MAINNET" | "TESTNET" | "REGTEST"
 */
export type NetworkTypeKey = keyof typeof NetworkType;

// ============================================================================
// 4. MAP: Network Key → Network Value
// ============================================================================

/**
 * Maps uppercase network keys to lowercase network values.
 * Example: { MAINNET: "mainnet", TESTNET: "testnet", REGTEST: "regtest" }
 */
export const NetworkMap = { ...NetworkType } satisfies {
  [K in NetworkTypeKey]: NetworkType;
};

// ============================================================================
// 5. REVERSE MAP: Network Value → Network Key
// ============================================================================

/**
 * Maps lowercase network values to uppercase network keys.
 * This is the reverse of NetworkMap.
 * Example: { "mainnet": "MAINNET", "testnet": "TESTNET", "regtest": "REGTEST" }
 */
export const NetworkReverseMap = Object.fromEntries(
  Object.entries(NetworkType).map(([upper, lower]) => [lower, upper])
) as Record<NetworkTypeValue, NetworkTypeKey>;

// ============================================================================
// 6. UTILITY FUNCTIONS
// ============================================================================

/**
 * Converts a network key (uppercase) to its corresponding value (lowercase).
 * @param n - Network key (e.g., "MAINNET")
 * @returns Network value (e.g., "mainnet")
 * @example networkKeyToValue("MAINNET") // returns "mainnet"
 */
export function networkKeyToValue(n: NetworkTypeKey): NetworkTypeValue {
  return NetworkMap[n];
}

/**
 * Converts a network value (lowercase) to its corresponding key (uppercase).
 * @param n - Network value (e.g., "mainnet")
 * @returns Network key (e.g., "MAINNET")
 * @example networkValueToKey("mainnet") // returns "MAINNET"
 */
export function networkValueToKey(n: NetworkTypeValue): NetworkTypeKey {
  return NetworkReverseMap[n];
}

/**
 * Checks if the given network is mainnet.
 * Accepts both uppercase keys and lowercase values.
 * @param n - Network to check (either key or value)
 * @returns true if the network is mainnet, false otherwise
 * @example isMainnet("MAINNET") // returns true
 * @example isMainnet("mainnet") // returns true
 * @example isMainnet("testnet") // returns false
 */
export function isMainnet(n: NetworkTypeValue | NetworkTypeKey): boolean {
  // Accept both "mainnet" and "MAINNET"
  if (n === NetworkReverseMap.mainnet || n === NetworkType.MAINNET) return true;
  return false;
}

/**
 * Checks if the given network is part of the testnet family (TESTNET or REGTEST).
 * Both testnet and regtest are non-production environments used for testing.
 * @param n - Network to check (either key or value)
 * @returns true if the network is TESTNET or REGTEST, false otherwise
 * @example isTestnetFamily("testnet") // returns true
 * @example isTestnetFamily("REGTEST") // returns true
 * @example isTestnetFamily("mainnet") // returns false
 */
export function isTestnetFamily(n: NetworkTypeValue | NetworkTypeKey): boolean {
  // Convert to uppercase key for consistent comparison
  const key =
    typeof n === 'string' && n in NetworkMap
      ? (n as NetworkTypeKey)
      : networkValueToKey(n as NetworkTypeValue);
  // Both TESTNET and REGTEST are non-production environments
  return key === 'TESTNET' || key === 'REGTEST';
}
